import React, { useState } from 'react';

import { Link } from '@change/ds-core-ui';
import { FacebookIcon, RedditIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Card, CardBody, CardFooter, CardHeader, Image, Text } from '@change/ds-core-ui/next';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import type { Recommendation } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { RootShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useShareRecommendationsTracking } from '../../hooks/useShareRecommendationsTracking';
import type { Props as MainProps } from '../../index';

type Platform = keyof RootShareRecommendations;

function FacebookIconImage() {
	return (
		<div className="flex h-full w-full items-center justify-center bg-[#D1E4FC]" data-testid="facebook-icon">
			<div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-white bg-[#1877F2]">
				<FacebookIcon className="text-white" size={42} />
			</div>
		</div>
	);
}

function RedditIconImage() {
	return (
		<div className="flex h-full w-full items-center justify-center bg-[#FFC7B2]" data-testid="reddit-icon">
			<div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-white bg-[#FF4500]">
				<RedditIcon className="text-white" size={42} />
			</div>
		</div>
	);
}

function RedditFooter() {
	return (
		<div className="flex flex-row items-center gap-2 align-middle">
			<div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#FF6A33] text-white">
				<RedditIcon size={12} />
			</div>
			<Text
				className="text-typography-weak flex-grow text-ellipsis align-middle leading-none"
				size="x-small"
				fontWeight="light"
			>
				<Translate value="corgi.petition.starter_dashboard_v2.recommendations.reddit.subreddit" />
			</Text>
		</div>
	);
}

function FacebookFooter() {
	return (
		<div className="flex flex-row items-center gap-2 align-middle">
			<div className="flex h-4 w-4 items-center justify-center rounded-full bg-[#1877F2] text-[#1877F2]">
				<FacebookIcon size={16} className="bg-white" />
			</div>
			<Text className="text-typography-weak flex-grow align-middle leading-none" size="x-small" fontWeight="light">
				<Translate value="corgi.petition.starter_dashboard_v2.recommendations.facebook.group" />
			</Text>
		</div>
	);
}

/*
	Using blank alt attribute to prevent screen readers from reading the images. This is needed because the
	images are decorative and the whole component is wrapped in a link. Screen readers would announce twice or
	even three times the same information if we don't use blank alt attribute.
 */
function RedditImageHeader({ imageUrl, onImageError }: { imageUrl: string; onImageError: () => void }) {
	return (
		<div className="relative flex h-full w-full items-center justify-center overflow-hidden">
			<div className="absolute inset-0 flex h-full w-full items-center justify-center">
				<Image src={imageUrl} alt="" className="z-0 h-96 w-96 object-cover blur-lg" />
			</div>
			<div className="relative z-[5] flex h-full w-full items-center justify-center">
				<Image
					src={imageUrl}
					alt=""
					className="h-20 w-20 rounded-full border-2 border-solid border-white object-contain"
					data-testid="social-card-image"
					onError={onImageError}
				/>
			</div>
		</div>
	);
}

function FacebookImageHeader({ imageUrl, onImageError }: { imageUrl: string; onImageError: () => void }) {
	return (
		<div className="flex h-full w-full items-center justify-center overflow-hidden">
			<div className="z-[5] flex h-full w-full items-center justify-center">
				<Image
					src={imageUrl}
					alt=""
					className="h-full w-full object-cover"
					data-testid="social-card-image"
					onError={onImageError}
				/>
			</div>
		</div>
	);
}

/**
 * Platform-specific UI components and metadata for social group cards.
 * If adding more platforms, considering switching this implementation
 * over to using thin wrapper components per platform, passing this sub-components
 * as props to SocialGroupCard
 */
const PLATFORM_UI: Record<
	Platform,
	{
		Header: React.FC<{ imageUrl: string; onImageError: () => void }>;
		DefaultHeader: React.FC;
		Footer: React.FC;
		linkLocation: string;
		ariaTag: string;
	}
> = {
	facebook: {
		Header: FacebookImageHeader,
		DefaultHeader: FacebookIconImage,
		Footer: FacebookFooter,
		linkLocation: 'facebook_group',
		ariaTag: 'Facebook Group',
	},
	reddit: {
		Header: RedditImageHeader,
		DefaultHeader: RedditIconImage,
		Footer: RedditFooter,
		linkLocation: 'reddit_group',
		ariaTag: 'Subreddit',
	},
} as const;

type Props = {
	recommendation: Recommendation;
	platform: Platform;
	isFirst?: boolean;
	isLast?: boolean;
	isReskin?: boolean;
	viewContext: MainProps['viewContext'];
};

export function SocialGroupCard({
	recommendation,
	platform,
	isFirst = false,
	isLast = false,
	isReskin = false,
	viewContext,
}: Props) {
	const { metadata } = recommendation;
	const {
		actions: { handleLinkClick },
	} = useShareRecommendationsTracking();
	const { localizeNumber } = useI18n();
	const { Header, DefaultHeader, Footer, linkLocation, ariaTag } = PLATFORM_UI[platform];
	const [fallbackToIcon, setFallbackToIcon] = useState(false);

	const firstElementMargin = isFirst && !isReskin ? 'ml-6' : '';
	const lastElementMargin = isLast ? 'mr-4' : '';

	return (
		<Link
			href={recommendation.url}
			aria-label={`${ariaTag}: ${recommendation.name}`}
			data-platform={platform}
			data-testid={`social-group-card-${platform}`}
			data-qa={`social-group-card-${platform}`}
			target="_blank"
			className={cn('snap-center no-underline', firstElementMargin, lastElementMargin)}
			onClick={() => {
				handleLinkClick(recommendation.name, linkLocation, metadata.source, viewContext);
			}}
			tabIndex={0}
		>
			<Card
				className={cn(
					'elevation-low flex h-full flex-col orientation-vertical:max-h-[308px] orientation-vertical:min-h-[260px] orientation-vertical:w-[160px] hover:shadow-lg',
					isReskin ? 'rounded-xl' : '',
				)}
				orientation="vertical"
			>
				<>
					<CardHeader variant="media" className="h-[130px]">
						{metadata.imageUrl && !fallbackToIcon ? (
							<Header imageUrl={metadata.imageUrl} onImageError={() => setFallbackToIcon(true)} />
						) : (
							<DefaultHeader />
						)}
					</CardHeader>
					<CardBody className="flex grow flex-col">
						<Text
							size="xx-small"
							variant="heading"
							// Only truncate on reddit, as subreddit names are only one word/have no spaces
							// Facebook groups tend to be multiple words, in which case, it's 'safe' to break the text
							// into multiple lines
							truncate={platform === 'reddit'}
							className="line-clamp-3"
							data-testid="recommendation-name"
						>
							{recommendation.name}
						</Text>
						{metadata.memberCount ? (
							<Text
								className="text-typography-weak grow-1 pt-1"
								fontWeight="light"
								size="small"
								data-testid="member-count"
							>
								<Translate
									value="corgi.petition.starter_dashboard_v2.recommendations.followers_count"
									replacements={{ followers_count: localizeNumber(metadata.memberCount) }}
								/>
							</Text>
						) : null}
					</CardBody>
					<CardFooter className="self-start">
						<Footer />
					</CardFooter>
				</>
			</Card>
		</Link>
	);
}
